import React, { useEffect, useState } from "react";
import Loader from "../../../../../../../Components/CircularLoader/CircularLoader";
import {
  confirmAddCard,
  getCardSetupIntent,
} from "../../../../../../../services/ShipperServices/Payments/payments.services";

import Backdrop from "@mui/material/Backdrop";
import Modal from "@mui/material/Modal";
import {
  CardElement,
  useStripe,
  useElements,
  Elements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";

const CardForm = ({ onClose, setCards, clientSecret }) => {
  const { t } = useTranslation("finances");
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError(t("card.notReady"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        { payment_method: { card: cardElement } }
      );

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      const confirm = await confirmAddCard(setupIntent.payment_method);
      if (!confirm?.error) {
        setCards((prev) => [...prev, confirm.data.result]);
      }

      onClose();
    } catch (err) {
      setError(err.message || t("card.addFailed"));
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* Card Input styled like a card */}
      <div className="bg-ui-surface shadow-md rounded-xl border border-ui-border p-4 flex flex-col gap-2 hover:shadow-lg transition">
        <label className="text-ui-text-muted text-sm">{t("card.details")}</label>
        <div className="bg-ui-surface-sunken p-3 rounded-lg border border-ui-border">
          <CardElement
            options={{
              hidePostalCode: true,
              style: {
                base: {
                  fontSize: "15px",
                  color: "var(--color-ui-text)",
                  "::placeholder": { color: "#9ca3af" },
                  fontFamily: "Inter, sans-serif",
                },
                invalid: { color: "#ef4444" },
              },
            }}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="text-state-danger text-sm bg-state-danger-bg p-2 rounded">
          {error}
        </div>
      )}

      {/* Footer Buttons */}
      <div className="flex justify-end gap-3 mt-2">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1.5 text-sm text-ui-text bg-ui-border hover:bg-ui-border rounded-lg transition"
        >
          {t("common:actions.cancel")}
        </button>

        <button
          type="submit"
          disabled={loading}
          className={`px-4 py-1.5 rounded-lg text-sm text-white flex items-center justify-center gap-2
            ${
              loading
                ? "bg-ui-text-subtle cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-700"
            }
          `}
        >
          {loading ? <Loader /> : t("card.addCard")}
        </button>
      </div>
    </form>
  );
};

export default function AddCard({ open, setOpen, setCards }) {
  const { t } = useTranslation("finances");
  const [stripePromise, setStripe] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loadingCard, setLoadingCard] = useState(false);

  const handleClose = () => setOpen(false);

  useEffect(() => {
    const loadStripeFunc = async () => {
      const stripeObj = await loadStripe(process.env.REACT_APP_STRIPE_KEY);
      setStripe(stripeObj);
    };

    const fetchIntent = async () => {
      setLoadingCard(true);
      const res = await getCardSetupIntent();
      setClientSecret(res?.data?.result?.client_secret);
      setLoadingCard(false);
    };

    loadStripeFunc();
    fetchIntent();
  }, []);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <div
        className="
          absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
          w-[500px] p-6 rounded-xl shadow-xl
          bg-ui-surface-sunken flex flex-col gap-4
        "
      >
        <h2 className="text-ui-text text-lg font-semibold mb-2">
          {t("card.addNew")}
        </h2>

        <div className="min-h-[120px]">
          {stripePromise && clientSecret ? (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <CardForm
                onClose={handleClose}
                setCards={setCards}
                clientSecret={clientSecret}
              />
            </Elements>
          ) : loadingCard ? (
            <div className="h-24 w-full bg-ui-border animate-pulse rounded flex items-center justify-center text-ui-text-muted text-sm">
              {t("card.loadingForm")}
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
